/**
 * Online panel: account, cloud-save sync (explicit conflict choice), showcase,
 * community browser (download/play/like/vote/comments/boards/report), friends.
 * Renders into #online-panel; all data flows through OnlineClient, and every
 * view keeps working read-only/offline with a visible banner.
 */
import { OnlineClient, type LevelSummary } from '../online/client';
import { t } from './strings';

export interface OnlineUiCallbacks {
  /** Parse + start a downloaded level; returns false if it failed validation. */
  onPlayOnlineLevel(levelText: string, onlineId: number): boolean;
  /** Current save payload for cloud sync. */
  collectSave(): string;
  /** Replace local save with a cloud copy (reloads profile state). */
  applySave(data: string): boolean;
  onClose(): void;
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

export class OnlineUi {
  private root: HTMLElement;
  private sort = 'recent';
  private search = '';
  /** Level id whose detail strip (board/comments) is expanded. */
  private expanded: number | null = null;
  private detailTab: 'board' | 'comments' = 'board';
  private lastSyncedRevision: number;
  /** 'main' or an author profile page. */
  private view: 'main' | { profile: string } = 'main';
  private showFavorites = false;

  constructor(
    root: HTMLElement,
    private readonly client: OnlineClient,
    private readonly cb: OnlineUiCallbacks,
  ) {
    this.root = root;
    this.lastSyncedRevision = Number(localStorage.getItem('shape-shifters.cloud-rev') ?? 0);
  }

  async show(): Promise<void> {
    await this.client.me();
    await this.render();
  }

  private setSyncedRevision(rev: number): void {
    this.lastSyncedRevision = rev;
    localStorage.setItem('shape-shifters.cloud-rev', String(rev));
  }

  // --- Rendering -------------------------------------------------------------------

  private async render(status = ''): Promise<void> {
    if (this.view !== 'main') {
      await this.renderProfile(this.view.profile);
      return;
    }
    const user = this.client.user;
    const offline = !this.client.online;
    const [showcase, browse] = await Promise.all([
      this.client.showcase(),
      this.showFavorites
        ? this.client
            .favorites()
            .then((r) =>
              r.ok ? { ok: true as const, value: { levels: r.value, hasMore: false } } : r,
            )
        : this.client.browse({ sort: this.sort, search: this.search }),
    ]);

    const account = user
      ? `<div class="stat-line"><span>${t('online.signed-in', { handle: esc(user.handle) })}</span>
           <button class="small" id="on-logout">${t('online.logout')}</button></div>
         <div class="ed-cat">${t('online.cloud')}</div>
         <div class="stat-line"><span id="on-sync-status">${status}</span>
           <button class="small" id="on-sync">${t('online.sync')}</button></div>`
      : `<p class="hint">${t('gate.use-start-screen')}</p>`;

    const card = (l: LevelSummary, tag = ''): string => `
      <div class="ach online-level" data-lid="${l.id}">
        <b>${tag}${esc(l.name)}</b>
        <span class="online-meta">by
          <button class="linklike" data-author="${esc(l.author)}">${esc(l.author)}</button>
          · ${l.mode} · diff ${l.authorDifficulty ?? '?'}${
            l.communityDifficulty !== null ? ` (community ${l.communityDifficulty})` : ''
          } · ♥ ${l.likes} · ▶ ${l.downloads}</span>
        <div class="card-actions">
          <button class="small" data-play="${l.id}">${t('online.play')}</button>
          <button class="small" data-like="${l.id}" title="${t('online.like')}">♥</button>
          <button class="small" data-fav="${l.id}" title="${t('online.favorite')}">☆</button>
          <button class="small" data-detail="${l.id}">${t('online.board')} / ${t('online.comments')}</button>
          ${
            this.client.user && this.client.user.role !== 'player'
              ? `<button class="small" data-feature="${l.id}" title="${t('online.mod.feature')}">★</button>
                 <button class="small" data-schedule="${l.id}" title="${t('online.mod.schedule')}">☀</button>`
              : ''
          }
        </div>
        ${this.expanded === l.id ? `<div class="online-detail" id="on-detail-${l.id}"></div>` : ''}
      </div>`;

    let showcaseHtml = `<span class="hint">${t('online.no-results')}</span>`;
    if (showcase.ok) {
      const parts: string[] = [];
      if (showcase.value.daily) parts.push(card(showcase.value.daily, `☀ ${t('online.daily')}: `));
      if (showcase.value.weekly)
        parts.push(card(showcase.value.weekly, `◈ ${t('online.weekly')}: `));
      for (const f of showcase.value.featured.slice(0, 5)) parts.push(card(f, '★ '));
      if (parts.length > 0) showcaseHtml = parts.join('');
    }

    const browseHtml = browse.ok
      ? browse.value.levels.map((l) => card(l)).join('') ||
        `<span class="hint">${t('online.no-results')}</span>`
      : `<span class="hint">${t('online.no-results')}</span>`;

    this.root.innerHTML = `
      <h2>${t('online.title')}</h2>
      ${offline ? `<p class="hint">${t('online.offline')}</p>` : ''}
      ${account}
      <div class="ed-cat">${t('online.showcase')}</div>
      <div class="ach-list">${showcaseHtml}</div>
      <div class="ed-cat">${t('online.browse')}</div>
      <div class="online-controls">
        <input id="on-search" type="search" placeholder="${t('online.search')}" value="${esc(this.search)}">
        <select id="on-sort">
          ${['recent', 'downloads', 'likes', 'difficulty']
            .map(
              (s) =>
                `<option value="${s}" ${s === this.sort ? 'selected' : ''}>${t(`online.sort.${s}`)}</option>`,
            )
            .join('')}
        </select>
        ${
          user
            ? `<button class="small ${this.showFavorites ? 'primary' : ''}" id="on-favs">
                ${this.showFavorites ? t('online.all-levels') : t('online.favorites')}</button>`
            : ''
        }
      </div>
      <div class="ach-list" id="on-browse">${browseHtml}</div>
      ${user ? `<div class="ed-cat">${t('online.inbox')}</div><div id="on-inbox"></div>` : ''}
      ${user ? `<div class="ed-cat">${t('online.friends')}</div><div id="on-friends"></div>` : ''}
      ${
        user && user.role !== 'player'
          ? `<div class="ed-cat">${t('online.mod')}</div><div id="on-mod"></div>`
          : ''
      }
      <button class="small" id="on-close">${t('online.close')}</button>
    `;
    this.bind();
    if (this.expanded !== null) void this.renderDetail(this.expanded);
    if (user) {
      void this.renderFriends();
      void this.renderInbox();
      if (user.role !== 'player') void this.renderMod();
    }
  }

  private bind(): void {
    this.root.querySelector('#on-close')?.addEventListener('click', () => this.cb.onClose());
    this.root.querySelector('#on-logout')?.addEventListener('click', () => {
      void this.client.logout().then(() => this.render());
    });
    this.root.querySelector('#on-sync')?.addEventListener('click', () => void this.sync());
    const search = this.root.querySelector<HTMLInputElement>('#on-search');
    search?.addEventListener('change', () => {
      this.search = search.value;
      void this.render();
    });
    const sort = this.root.querySelector<HTMLSelectElement>('#on-sort');
    sort?.addEventListener('change', () => {
      this.sort = sort.value;
      void this.render();
    });
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-play]')) {
      btn.addEventListener('click', () => void this.play(Number(btn.dataset.play)));
    }
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-like]')) {
      btn.addEventListener('click', () => {
        void this.client.like(Number(btn.dataset.like), true).then(() => this.render());
      });
    }
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-detail]')) {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.detail);
        this.expanded = this.expanded === id ? null : id;
        void this.render();
      });
    }
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-fav]')) {
      btn.addEventListener('click', () => {
        void this.client
          .favorite(Number(btn.dataset.fav), !this.showFavorites)
          .then(() => this.render());
      });
    }
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-author]')) {
      btn.addEventListener('click', () => {
        this.view = { profile: btn.dataset.author as string };
        void this.render();
      });
    }
    this.root.querySelector('#on-favs')?.addEventListener('click', () => {
      this.showFavorites = !this.showFavorites;
      void this.render();
    });
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-feature]')) {
      btn.addEventListener('click', () => {
        void this.client.modFeature(Number(btn.dataset.feature), true).then(() => this.render());
      });
    }
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-schedule]')) {
      btn.addEventListener('click', () => {
        const date = prompt(t('online.mod.schedule-ph'), new Date().toISOString().slice(0, 10));
        if (!date) return;
        // A YYYY-MM-DD schedules the Daily Shift; YYYY-Www the Weekly Gauntlet.
        const weekly = /^\d{4}-W\d{2}$/.test(date);
        void this.client
          .modSchedule(Number(btn.dataset.schedule), weekly ? null : date, weekly ? date : null)
          .then(() => this.render());
      });
    }
  }

  // --- Author profile view -----------------------------------------------------------

  private async renderProfile(handle: string): Promise<void> {
    const result = await this.client.profile(handle);
    if (!result.ok) {
      this.view = 'main';
      await this.render();
      return;
    }
    const p = result.value;
    const me = this.client.user;
    const levels = p.levels
      .map(
        (l) => `<div class="ach"><b>${esc(l.name)}</b>
          <span>diff ${l.authorDifficulty ?? '?'} · ♥ ${l.likes} · ▶ ${l.downloads}
          <button class="small" data-play="${l.id}">${t('online.play')}</button></span></div>`,
      )
      .join('');
    this.root.innerHTML = `
      <h2>${esc(p.handle)}</h2>
      <div class="stat-line"><span>${t('online.profile.joined', { date: p.joinedAt.slice(0, 10) })}</span>
        <b>${t('online.profile.published', { n: p.stats.published })} · ♥ ${p.stats.likes}
        · ▶ ${p.stats.downloads} · ${t('online.profile.clears', { n: p.stats.clears })}</b></div>
      ${
        me && me.handle !== p.handle
          ? `<div class="ed-row">
              <button class="small" id="prof-friend">+ ${t('online.friends')}</button>
              <button class="small" id="prof-block">${t('online.block')}</button>
              <input id="prof-msg" type="text" maxlength="1000" placeholder="${t('online.message-ph')}">
              <button class="small" id="prof-send">${t('online.message')}</button>
            </div>`
          : ''
      }
      <div class="ach-list">${levels || `<span class="hint">${t('online.no-results')}</span>`}</div>
      <button class="small" id="prof-back">${t('online.profile.back')}</button>
    `;
    this.root.querySelector('#prof-back')?.addEventListener('click', () => {
      this.view = 'main';
      void this.render();
    });
    this.root.querySelector('#prof-friend')?.addEventListener('click', () => {
      void this.client.friendRequest(p.handle);
    });
    this.root.querySelector('#prof-block')?.addEventListener('click', () => {
      void this.client.block(p.handle, true).then(() => {
        this.view = 'main';
        void this.render();
      });
    });
    this.root.querySelector('#prof-send')?.addEventListener('click', () => {
      const input = this.root.querySelector<HTMLInputElement>('#prof-msg');
      if (input && input.value.trim()) {
        void this.client.sendMessage(p.handle, input.value.trim()).then(() => {
          if (input) input.value = '';
        });
      }
    });
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('[data-play]')) {
      btn.addEventListener('click', () => void this.play(Number(btn.dataset.play)));
    }
  }

  // --- Inbox -------------------------------------------------------------------------

  private async renderInbox(): Promise<void> {
    const host = this.root.querySelector('#on-inbox');
    if (!host) return;
    const inbox = await this.client.inbox();
    if (!inbox.ok) return;
    host.innerHTML =
      inbox.value
        .map(
          (m) => `<div class="stat-line ${m.readAt ? '' : 'unread'}" data-msg="${m.id}">
            <span><b>${esc(m.sender)}</b> ${esc(m.body)}</span>
            <b>${m.readAt ? '' : '●'}</b></div>`,
        )
        .join('') || `<span class="hint">${t('online.no-results')}</span>`;
    for (const row of host.querySelectorAll<HTMLElement>('[data-msg]')) {
      row.addEventListener('click', () => {
        void this.client.markRead(Number(row.dataset.msg)).then(() => this.renderInbox());
      });
    }
  }

  // --- Moderation (visible to moderator/admin only; server re-checks roles) ----------

  private async renderMod(): Promise<void> {
    const host = this.root.querySelector('#on-mod');
    if (!host) return;
    const reports = await this.client.modReports();
    if (!reports.ok) return;
    host.innerHTML =
      `<div class="hint">${t('online.mod.queue')}</div>` +
      (reports.value
        .map(
          (r) => `<div class="ach" data-report="${r.id}">
            <b>#${r.id} ${esc(r.subjectType)}:${r.subjectId}</b>
            <span>${esc(r.reason)} — ${esc(r.reporter)}
              <input type="text" maxlength="500" class="mod-reason" placeholder="${t('online.mod.reason-ph')}">
              <button class="small" data-resolve="${r.id}">${t('online.mod.resolve')}</button>
              ${
                r.subjectType === 'level'
                  ? `<button class="small danger" data-rmlevel="${r.subjectId}" data-from="${r.id}">${t('online.mod.remove-level')}</button>`
                  : ''
              }
              ${
                r.subjectType === 'comment'
                  ? `<button class="small danger" data-rmcomment="${r.subjectId}">${t('online.mod.remove-comment')}</button>`
                  : ''
              }
            </span></div>`,
        )
        .join('') || `<span class="hint">${t('online.no-results')}</span>`);
    const reason = (id: number): string =>
      host.querySelector<HTMLInputElement>(`[data-report="${id}"] .mod-reason`)?.value.trim() ||
      'resolved';
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-resolve]')) {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.resolve);
        void this.client.modResolve(id, reason(id)).then(() => this.renderMod());
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-rmlevel]')) {
      btn.addEventListener('click', () => {
        const from = Number(btn.dataset.from);
        void this.client
          .modRemoveLevel(Number(btn.dataset.rmlevel), reason(from))
          .then(() => this.renderMod());
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-rmcomment]')) {
      btn.addEventListener('click', () => {
        void this.client
          .modRemoveComment(Number(btn.dataset.rmcomment))
          .then(() => this.renderMod());
      });
    }
  }

  // --- Cloud sync -------------------------------------------------------------------

  private async sync(): Promise<void> {
    if (!this.client.user) return;
    const local = this.cb.collectSave();
    const put = await this.client.putSave(local, this.lastSyncedRevision || null);
    if (!put.ok) {
      await this.render(put.error);
      return;
    }
    if ('revision' in put.value) {
      this.setSyncedRevision(put.value.revision);
      await this.render(t('online.sync-ok', { rev: put.value.revision }));
      return;
    }
    // Conflict: both this device and the cloud moved. The player chooses.
    const server = put.value.conflict;
    const statusEl = this.root.querySelector('#on-sync-status');
    if (!statusEl) return;
    statusEl.innerHTML = `
      ${t('online.sync-conflict')}
      <button class="small" id="on-keep-local">${t('online.keep-local')}</button>
      <button class="small" id="on-keep-cloud">${t('online.keep-cloud')}</button>`;
    this.root.querySelector('#on-keep-local')?.addEventListener('click', () => {
      void (async () => {
        const forced = await this.client.putSave(local, server.revision);
        if (forced.ok && 'revision' in forced.value) this.setSyncedRevision(forced.value.revision);
        await this.render(t('online.sync-ok', { rev: this.lastSyncedRevision }));
      })();
    });
    this.root.querySelector('#on-keep-cloud')?.addEventListener('click', () => {
      if (this.cb.applySave(server.data)) this.setSyncedRevision(server.revision);
      void this.render(t('online.sync-ok', { rev: server.revision }));
    });
  }

  // --- Level actions ----------------------------------------------------------------

  private async play(id: number): Promise<void> {
    const text = await this.client.download(id);
    if (!text.ok) return;
    // Cache for offline replay of downloaded levels.
    localStorage.setItem(`shape-shifters.dl.${id}`, text.value);
    if (this.cb.onPlayOnlineLevel(text.value, id)) this.cb.onClose();
  }

  private async renderDetail(id: number): Promise<void> {
    const host = this.root.querySelector(`#on-detail-${id}`);
    if (!host) return;
    const tabs = `
      <div class="ed-row">
        <button class="small ${this.detailTab === 'board' ? 'primary' : ''}" data-tab="board">${t('online.board')}</button>
        <button class="small ${this.detailTab === 'comments' ? 'primary' : ''}" data-tab="comments">${t('online.comments')}</button>
        <select id="on-vote"><option value="">${t('online.vote')}</option>
          ${Array.from({ length: 10 }, (_, i) => `<option>${i + 1}</option>`).join('')}</select>
        <button class="small" data-report="${id}">${t('online.report')}</button>
      </div>`;
    let bodyHtml = '';
    if (this.detailTab === 'board') {
      const board = await this.client.leaderboard(id, 'global');
      bodyHtml = board.ok
        ? board.value
            .map(
              (r, i) =>
                `<div class="stat-line"><span>#${i + 1} ${esc(r.handle)}</span><b>${r.percent}% · ${(r.timeMs / 1000).toFixed(1)}s</b></div>`,
            )
            .join('') || `<span class="hint">${t('online.no-results')}</span>`
        : `<span class="hint">${t('online.no-results')}</span>`;
    } else {
      const comments = await this.client.comments(id);
      const list = comments.ok
        ? comments.value
            .map((c) => `<div class="ach"><b>${esc(c.author)}</b><span>${esc(c.body)}</span></div>`)
            .join('')
        : '';
      bodyHtml = `${list || `<span class="hint">${t('online.no-results')}</span>`}
        <div class="ed-row"><input id="on-comment" type="text" maxlength="500" placeholder="${t('online.comment-ph')}">
        <button class="small" id="on-comment-send">${t('online.send')}</button></div>`;
    }
    host.innerHTML = tabs + bodyHtml;

    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-tab]')) {
      btn.addEventListener('click', () => {
        this.detailTab = btn.dataset.tab as 'board' | 'comments';
        void this.renderDetail(id);
      });
    }
    host.querySelector<HTMLSelectElement>('#on-vote')?.addEventListener('change', (e) => {
      const v = Number((e.target as HTMLSelectElement).value);
      if (v >= 1) void this.client.vote(id, v).then(() => this.render());
    });
    host.querySelector('#on-comment-send')?.addEventListener('click', () => {
      const input = host.querySelector<HTMLInputElement>('#on-comment');
      if (input && input.value.trim()) {
        void this.client.postComment(id, input.value.trim()).then(() => this.renderDetail(id));
      }
    });
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-report]')) {
      btn.addEventListener('click', () => {
        const reason = prompt(t('online.report-ph'));
        if (reason) void this.client.report('level', id, reason);
      });
    }
  }

  private async renderFriends(): Promise<void> {
    const host = this.root.querySelector('#on-friends');
    if (!host) return;
    const friends = await this.client.friends();
    const rows = friends.ok
      ? friends.value
          .map((f) => {
            const action =
              f.status === 'pending' && !f.outgoing
                ? `<button class="small" data-accept="${esc(f.handle)}">${t('online.accept')}</button>`
                : f.status === 'pending'
                  ? `<span class="hint">${t('online.pending')}</span>`
                  : '';
            return `<div class="stat-line"><span>${esc(f.handle)}</span><b>${action}</b></div>`;
          })
          .join('')
      : '';
    host.innerHTML = `${rows}
      <div class="ed-row"><input id="on-friend-handle" type="text" maxlength="20" placeholder="${t('online.add-friend')}">
      <button class="small" id="on-friend-add">+</button></div>`;
    host.querySelector('#on-friend-add')?.addEventListener('click', () => {
      const input = host.querySelector<HTMLInputElement>('#on-friend-handle');
      if (input && input.value.trim()) {
        void this.client.friendRequest(input.value.trim()).then(() => this.renderFriends());
      }
    });
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-accept]')) {
      btn.addEventListener('click', () => {
        void this.client
          .friendAccept(btn.dataset.accept as string)
          .then(() => this.renderFriends());
      });
    }
  }
}
